import React, { useEffect } from 'react';
import { Lock, Eye, Shield, Database, Globe, UserCheck, Bell, Mail, Cookie, AlertTriangle } from 'lucide-react';

const Privacy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    {
      icon: Eye,
      title: '1. Information We Collect',
      content: `
        We collect information to provide better services to our users and clients. The types of information we collect include:
        
        Information You Provide Directly:
        • Personal identification: Name, email address, phone number, company name
        • Account information: Username, password (encrypted), account preferences
        • Business information: Company details, project requirements, business goals
        • Payment information: Billing address, payment method details (processed securely through third-party providers)
        • Communications: Messages, feedback, support requests, and other correspondence
        • Professional information: Job title, industry, professional background
        
        Information Collected Automatically:
        • Device information: IP address, browser type, operating system, device identifiers
        • Usage data: Pages visited, features used, time spent on site, click patterns
        • Location data: General geographic location based on IP address
        • Cookies and similar technologies: Session data, preferences, authentication tokens
        • Log data: Server logs, error reports, performance metrics
        • Analytics data: Aggregated usage patterns and trends
        
        Information from Third Parties:
        • Social media profiles (if you choose to connect them)
        • Business verification services for corporate clients
        • Marketing partners and lead generation services
        • Publicly available business directories
        • Credit verification services (for payment processing)
      `
    },
    {
      icon: Database,
      title: '2. How We Use Your Information',
      content: `
        We use the collected information for various purposes essential to our business operations and service delivery:
        
        Service Delivery and Management:
        • Providing, maintaining, and improving our application development services
        • Managing your account and authenticating your identity
        • Processing transactions and sending transaction notifications
        • Customizing services based on your preferences and needs
        • Providing customer support and responding to inquiries
        • Delivering project updates and milestone notifications
        
        Communication and Marketing:
        • Sending service-related announcements and updates
        • Providing technical notices and security alerts
        • Sending promotional communications (with your consent)
        • Conducting surveys and collecting feedback
        • Sharing company news and industry insights
        • Notifying you about changes to our services or policies
        
        Business Operations:
        • Analyzing usage patterns to improve our services
        • Conducting research and development
        • Training our team and improving service quality
        • Preventing fraud and enhancing security
        • Complying with legal obligations and regulations
        • Protecting our rights and the rights of our users
        
        Legal Bases for Processing (GDPR):
        • Consent: When you've given clear consent for specific purposes
        • Contract: When processing is necessary to fulfill our contract with you
        • Legal obligations: When we must process data to comply with laws
        • Legitimate interests: When processing serves our legitimate business interests without overriding your rights
      `
    },
    {
      icon: Shield,
      title: '3. Data Security and Protection',
      content: `
        We implement comprehensive security measures to protect your information from unauthorized access, alteration, disclosure, or destruction:
        
        Technical Safeguards:
        • Industry-standard encryption (TLS/SSL) for data in transit
        • Encryption at rest for sensitive data storage
        • Secure data centers with physical access controls
        • Regular security audits and vulnerability assessments
        • Intrusion detection and prevention systems
        • Multi-factor authentication for sensitive operations
        • Regular automated backups with encryption
        • Secure code development practices
        
        Organizational Measures:
        • Limited access to personal information on a need-to-know basis
        • Confidentiality agreements with all employees and contractors
        • Regular security training for our team
        • Incident response procedures and breach notification protocols
        • Data protection impact assessments for new projects
        • Vendor security assessments for third-party services
        • Regular review and update of security policies
        
        Data Retention:
        • We retain personal data only as long as necessary for business purposes
        • Account data is retained while your account is active
        • Project data is retained according to contractual agreements
        • Marketing data is retained until you opt-out
        • Legal and financial records are retained as required by law
        • Backup data is retained for disaster recovery purposes
        • You can request data deletion subject to legal requirements
        
        Breach Notification:
        In the unlikely event of a data breach, we will:
        • Notify affected users within 72 hours of discovery
        • Provide details about the nature and scope of the breach
        • Offer guidance on protective measures you can take
        • Cooperate with regulatory authorities as required
      `
    },
    {
      icon: Globe,
      title: '4. Information Sharing and Disclosure',
      content: `
        We respect your privacy and limit sharing of your information. We do not sell, trade, or rent your personal information to third parties. We may share your information in the following circumstances:
        
        Service Providers:
        • Cloud hosting providers (AWS, Google Cloud, Azure)
        • Payment processors (Stripe, PayPal)
        • Email service providers for communication
        • Analytics providers for service improvement
        • Customer support tools and platforms
        • Project management and collaboration tools
        All service providers are bound by confidentiality agreements and can only use your data to provide services to us.
        
        Business Transfers:
        • In case of merger, acquisition, or sale of assets, your information may be transferred
        • We will notify you before your information becomes subject to different privacy policies
        • You will have the option to close your account if you disagree with the transfer
        
        Legal Requirements:
        We may disclose your information when required to:
        • Comply with legal obligations, court orders, or government requests
        • Enforce our terms of service and other agreements
        • Protect the rights, property, or safety of HyperionAI, our users, or the public
        • Investigate potential violations or fraud
        • Respond to lawful requests by public authorities
        
        With Your Consent:
        • When you explicitly consent to share information with specific third parties
        • For testimonials or case studies (with your prior written approval)
        • When you participate in public forums or communities
        • For co-marketing initiatives you've agreed to participate in
        
        Aggregated Data:
        We may share aggregated, anonymized data that cannot identify you personally for:
        • Industry reports and research
        • Marketing and business development
        • Service improvement and innovation
      `
    },
    {
      icon: UserCheck,
      title: '5. Your Rights and Choices',
      content: `
        You have control over your personal information and how we use it. Depending on your location, you may have the following rights:
        
        Access and Portability:
        • Request a copy of the personal information we hold about you
        • Receive your data in a structured, commonly used format
        • Transfer your data to another service provider
        • Review and verify the accuracy of your data
        
        Correction and Deletion:
        • Update or correct inaccurate information
        • Request deletion of your personal information
        • Restrict processing of your data in certain circumstances
        • Object to certain uses of your information
        
        Communication Preferences:
        • Opt-out of marketing communications at any time
        • Manage notification preferences in your account settings
        • Unsubscribe from emails via the link in each message
        • Control cookie preferences through your browser settings
        
        Account Management:
        • Close your account at any time
        • Export your data before account closure
        • Request data retention limitations
        • Manage connected third-party services
        
        Exercising Your Rights:
        To exercise any of these rights, contact us at support@hyperionai-ltd.com. We will:
        • Respond to your request within 30 days
        • Verify your identity to protect your privacy
        • Provide clear explanations if we cannot fulfill a request
        • Never discriminate against you for exercising your rights
        
        Complaints:
        If you're unsatisfied with our response, you may:
        • Contact our Data Protection Officer
        • File a complaint with your local data protection authority
        • Seek remedy through applicable courts
      `
    },
    {
      icon: Cookie,
      title: '6. Cookies and Tracking Technologies',
      content: `
        We use cookies and similar tracking technologies to improve your experience and gather information about how our services are used:
        
        Types of Cookies We Use:
        
        Essential Cookies:
        • Required for basic site functionality
        • Enable secure areas and authentication
        • Remember your preferences during a session
        • Cannot be disabled without losing core functionality
        
        Performance Cookies:
        • Help us understand how visitors interact with our site
        • Collect anonymous, aggregated data
        • Used to improve site performance and user experience
        • Can be disabled through browser settings
        
        Functionality Cookies:
        • Remember your preferences and settings
        • Provide personalized features
        • Enable social media integration
        • Enhance your user experience
        
        Marketing Cookies:
        • Track your visits across websites
        • Used to display relevant advertisements
        • Measure the effectiveness of marketing campaigns
        • Can be opted out through cookie preferences
        
        Third-Party Cookies:
        • Google Analytics for usage analysis
        • Social media plugins for content sharing
        • Chat and support widgets for customer service
        • Payment processors for transaction handling
        
        Managing Cookies:
        • Most browsers allow you to control cookies through settings
        • You can delete existing cookies from your browser
        • You can set your browser to refuse cookies
        • Some features may not function properly without cookies
        • We provide a cookie consent banner for your preferences
        
        Other Tracking Technologies:
        • Web beacons to track email opens and clicks
        • Local storage for temporary data caching
        • Session storage for maintaining state
        • Device fingerprinting for security purposes
      `
    },
    {
      icon: Bell,
      title: '7. Children\'s Privacy',
      content: `
        Protection of children's privacy is important to us:
        
        Age Restrictions:
        • Our services are not directed to children under 16
        • We do not knowingly collect information from children under 16
        • Business services are intended for adult business users only
        • Educational institution clients must ensure appropriate consent
        
        If We Discover Child Data:
        • We will promptly delete the information
        • We will notify the account holder if applicable
        • We will take steps to prevent future collection
        
        Parental Rights:
        If you believe we have collected information from a child under 16:
        • Contact us immediately at support@hyperionai-ltd.com
        • We will investigate and take appropriate action
        • Parents can request deletion of their child's information
        
        Educational Services:
        • When working with educational institutions, we rely on school consent
        • We limit data collection to educational purposes only
        • We provide special protections for student data
        • We comply with FERPA and similar educational privacy laws
      `
    },
    {
      icon: Globe,
      title: '8. International Data Transfers',
      content: `
        As a global service provider, we may transfer your information across borders:
        
        Transfer Mechanisms:
        • We operate globally and may process data in various countries
        • We use Standard Contractual Clauses approved by the European Commission
        • We ensure adequate protection regardless of location
        • We comply with Privacy Shield principles where applicable
        
        Data Localization:
        • We respect local data residency requirements
        • You can request information about where your data is processed
        • We maintain data centers in multiple regions for performance
        • Backup data may be stored in different geographic locations
        
        Your Rights Regarding Transfers:
        • You can request details about international transfers
        • You can object to transfers in certain circumstances
        • We will provide appropriate safeguards for all transfers
        • You can request a copy of our transfer agreements (with confidential information redacted)
        
        Regional Compliance:
        • GDPR (European Union): Full compliance with all requirements
        • CCPA (California): Consumer rights and transparency
        • LGPD (Brazil): Local representative and data protection
        • PIPEDA (Canada): Privacy principles and consent
        • APEC Privacy Framework: Cross-border privacy rules
      `
    },
    {
      icon: AlertTriangle,
      title: '9. Updates to This Policy',
      content: `
        We may update this Privacy Policy to reflect changes in our practices, technologies, legal requirements, and other factors:
        
        Notification of Changes:
        • We will notify you of material changes via email or prominent website notice
        • Minor changes may be made without notification
        • The "Last Updated" date at the top reflects the most recent revision
        • We encourage you to review this policy periodically
        
        Types of Changes:
        • Clarifications and minor corrections
        • New features or services requiring data collection
        • Legal or regulatory compliance updates
        • Changes in data retention or sharing practices
        • Updates to contact information or procedures
        
        Your Options:
        • Continue using our services indicates acceptance of updated terms
        • You can close your account if you disagree with changes
        • You can export your data before closing your account
        • Previous versions can be requested for comparison
        
        Transition Period:
        • Material changes will include a 30-day notice period
        • During this time, the previous policy remains in effect
        • You can make informed decisions about continued use
        • We will clearly highlight what has changed
      `
    },
    {
      icon: Mail,
      title: '10. Contact Us',
      content: `
        We welcome your questions, comments, and concerns about privacy:
        
        Primary Contact:
        Email: support@hyperionai-ltd.com
        Response Time: Within 2 business days
        Subject Line: Include "Privacy Inquiry" for faster routing
        
        Data Protection Officer:
        For privacy-specific concerns and GDPR inquiries
        Email: support@hyperionai-ltd.com
        Role: Oversees data protection strategy and compliance
        
        What to Include in Your Request:
        • Your name and account information
        • Specific privacy concern or request
        • Relevant dates or transaction IDs if applicable
        • Preferred method of response
        
        Alternative Contact Methods:
        • Through your account dashboard
        • Via our secure contact form on the website
        • By postal mail to our registered address
        
        Escalation Process:
        1. Initial inquiry to support team
        2. Escalation to Data Protection Officer if needed
        3. Senior management review for complex issues
        4. External mediation or regulatory involvement if necessary
        
        We are committed to resolving any privacy concerns promptly and thoroughly.
      `
    }
  ];

  return (
    <div className="min-h-screen bg-dark-bg pt-24 pb-20">
      <div className="max-w-4xl mx-auto content-padding">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Privacy <span className="brand-gradient">Policy</span>
          </h1>
          <p className="text-dark-muted">
            Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>

        {/* Introduction */}
        <div className="bg-dark-surface border border-dark-border rounded-xl p-6 mb-8">
          <div className="flex items-start gap-4 mb-4">
            <Lock className="w-6 h-6 text-brand flex-shrink-0 mt-1" />
            <div>
              <h2 className="text-xl font-semibold mb-2">Your Privacy Matters</h2>
              <p className="text-dark-muted leading-relaxed">
                At HyperionAI, we are committed to protecting your privacy and ensuring the security of your personal information. 
                This Privacy Policy explains how we collect, use, share, and protect your information when you use our services, 
                visit our website, or interact with us. We adhere to global privacy standards including GDPR, CCPA, and other 
                applicable privacy regulations to ensure your data is handled with the utmost care and transparency.
              </p>
            </div>
          </div>
        </div>

        {/* Quick Navigation */}
        <div className="bg-gradient-to-br from-brand/10 to-brand-light/10 border border-brand/30 rounded-xl p-6 mb-8">
          <h3 className="text-lg font-semibold mb-4">Quick Navigation</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {sections.map((section, index) => (
              <button
                key={index}
                onClick={() => document.getElementById(`section-${index}`).scrollIntoView({ behavior: 'smooth' })}
                className="text-left text-sm text-dark-muted hover:text-brand transition-colors duration-300"
              >
                {section.title}
              </button>
            ))}
          </div>
        </div>

        {/* Sections */}
        <div className="space-y-8">
          {sections.map((section, index) => {
            const Icon = section.icon;
            return (
              <div 
                key={index} 
                id={`section-${index}`}
                className="bg-dark-surface border border-dark-border rounded-xl p-6 hover:border-brand/30 transition-colors duration-300"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-10 h-10 bg-brand/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-brand" />
                  </div>
                  <h2 className="text-xl font-semibold">{section.title}</h2>
                </div>
                <div className="text-dark-muted leading-relaxed whitespace-pre-line pl-14">
                  {section.content.trim()}
                </div>
              </div>
            );
          })}
        </div>

        {/* Contact Section */}
        <div className="mt-12 bg-gradient-to-br from-brand/10 to-brand-light/10 border border-brand/30 rounded-xl p-8">
          <h3 className="text-2xl font-semibold mb-4">Questions About Privacy?</h3>
          <p className="text-dark-muted mb-6">
            Your privacy is important to us. If you have any questions about this Privacy Policy, 
            our data practices, or how we handle your personal information, please don't hesitate to contact us.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <a 
              href="mailto:support@hyperionai-ltd.com?subject=Privacy Policy Inquiry"
              className="action-btn inline-flex items-center justify-center gap-2"
            >
              <Mail className="w-5 h-5" />
              Contact Privacy Team
            </a>
            <button 
              onClick={() => window.print()}
              className="outline-btn inline-flex items-center justify-center gap-2"
            >
              <Shield className="w-5 h-5" />
              Print Privacy Policy
            </button>
          </div>
        </div>

        {/* Footer Note */}
        <div className="mt-8 text-center text-sm text-dark-muted">
          <p>
            © {new Date().getFullYear()} HyperionAI. All rights reserved. This Privacy Policy is part of our commitment to protecting your data.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Privacy;

